import React, { Dispatch, useEffect, useState } from 'react';
import styles from './ProjectModal.module.css';
import { Item } from '../../../types';
import Accordion from '../Accordion/Accordion';
import ImagePopup from '../ImagePopup/ImagePopup';

type Props = { project: Item; setModalContent: Dispatch<any> };

const ProjectModal = ({ project, setModalContent }: Props) => {
  const [closing, setClosing] = useState(false);

  const [imageContent, setImageContent] = useState<
    | {
        url: string;
        alt: string;
      }
    | undefined
  >(undefined);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setModalContent(undefined);
    }, 295);
  };

  useEffect(() => {
    document.body.classList.add('modal-open');

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        return handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`${styles.mainContainer} ${
        closing ? styles.mainContainerExit : null
      }`}
    >
      <div
        className={`${styles.contentContainer} ${
          closing ? styles.contentContainerExit : null
        }`}
      >
        <div className={styles.header}>
          <div className={styles.titleLogo}>
            <h2 className={styles.title}>{project.fields.title}</h2>
            <img
              className={styles.companyLogo}
              src={project.fields.companyLogo?.fields.file.url}
              alt="company logo"
            />
          </div>

          <button className={styles.closeButton} onClick={handleClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M18 6l-12 12" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={styles.content}>
          <p className={styles.description}>{project.fields.description}</p>

          <Accordion
            expandedDefault
            title={
              <h3 className={styles.accordionHeading}>
                Star breakdown{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M8.243 7.34l-6.38 .925l-.113 .023a1 1 0 0 0 -.44 1.684l4.622 4.499l-1.09 6.355l-.013 .11a1 1 0 0 0 1.464 .944l5.706 -3l5.693 3l.1 .046a1 1 0 0 0 1.352 -1.1l-1.091 -6.355l4.624 -4.5l.078 -.085a1 1 0 0 0 -.633 -1.62l-6.38 -.926l-2.852 -5.78a1 1 0 0 0 -1.794 0l-2.853 5.78z" />
                </svg>
              </h3>
            }
          >
            <div className={styles.mainStarContainer}>
              <div className={styles.starContent}>
                <h4 className={styles.starHeading}>Situation</h4>
                <p className={styles.starText}>
                  {project.fields.starBreakdown?.[0]}
                </p>
              </div>

              <div className={styles.starContent}>
                <h4 className={styles.starHeading}>Task</h4>
                <p className={styles.starText}>
                  {project.fields.starBreakdown?.[1]}
                </p>
              </div>

              <div className={styles.starContent}>
                <h4 className={styles.starHeading}>Action</h4>
                <p className={styles.starText}>
                  {project.fields.starBreakdown?.[2]}
                </p>
              </div>

              <div className={styles.starContent}>
                <h4 className={styles.starHeading}>Result</h4>
                <p className={styles.starText}>
                  {project.fields.starBreakdown?.[3]}
                </p>
              </div>
            </div>
          </Accordion>

          <Accordion
            title={
              <h3 className={styles.accordionHeading}>
                Technologies used{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M17 3.34a10 10 0 1 1 -15 8.66l.005 -.324a10 10 0 0 1 14.995 -8.336m-6.293 5.953a1 1 0 0 0 -1.414 0l-2 2a1 1 0 0 0 0 1.414l2 2a1 1 0 0 0 1.414 0l.083 -.094a1 1 0 0 0 -.083 -1.32l-1.292 -1.293l1.292 -1.293a1 1 0 0 0 0 -1.414m4 0a1 1 0 0 0 -1.414 0l-.083 .094a1 1 0 0 0 .083 1.32l1.292 1.293l-1.292 1.293a1 1 0 0 0 1.414 1.414l2 -2a1 1 0 0 0 0 -1.414z" />
                </svg>
              </h3>
            }
          >
            <div className={styles.techContainer}>
              <p className={styles.techDesc}>
                The technologies used in this project were selected based on
                their suitability, performance, and relevance at the time of
                development. Each was selected to best support the project's
                goals, ensuring reliability and efficient implementation.
              </p>

              <div className={styles.imageGridContainer}>
                {project.fields.technologyLogos?.map((logo) => (
                  <div
                    className={styles.techLogoTextContainer}
                    key={logo.fields.title}
                  >
                    <img
                      src={logo.fields.file.url}
                      alt="Tech used logo"
                      className={styles.techLogoImg}
                    />
                    <p className={styles.techLogoText}>{logo.fields.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </Accordion>

          <Accordion
            title={
              <h3 className={styles.accordionHeading}>
                Images{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M8.813 11.612c.457 -.38 .918 -.38 1.386 .011l.108 .098l4.986 4.986l.094 .083a1 1 0 0 0 1.403 -1.403l-.083 -.094l-1.292 -1.293l.292 -.293l.106 -.095c.457 -.38 .918 -.38 1.386 .011l.108 .098l4.674 4.675a4 4 0 0 1 -3.775 3.599l-.206 .005h-12a4 4 0 0 1 -3.98 -3.603l6.687 -6.69l.106 -.095zm9.187 -9.612a4 4 0 0 1 3.995 3.8l.005 .2v9.585l-3.293 -3.292l-.15 -.137c-1.256 -1.095 -2.85 -1.097 -4.096 -.017l-.154 .14l-.307 .306l-2.293 -2.292l-.15 -.137c-1.256 -1.095 -2.85 -1.097 -4.096 -.017l-.154 .14l-5.307 5.306v-9.585a4 4 0 0 1 3.8 -3.995l.2 -.005h12zm-2.99 5l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007z" />
                </svg>
              </h3>
            }
          >
            <div className={styles.imageGridContainer}>
              {project.fields.images?.map((image) => (
                <div
                  className={styles.imgZoomContainer}
                  key={image.fields.file.url}
                >
                  <img
                    key={image.fields.title}
                    className={styles.gridImage}
                    src={image.fields.file.url}
                    alt="project mockup"
                    onClick={() =>
                      setImageContent({
                        url: image.fields.file.url,
                        alt: 'mockup zoom',
                      })
                    }
                  />

                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                    <path d="M7 10l6 0" />
                    <path d="M10 7l0 6" />
                    <path d="M21 21l-6 -6" />
                  </svg>
                </div>
              ))}
            </div>
          </Accordion>
        </div>
      </div>
      {imageContent ? (
        <ImagePopup {...imageContent} setImageContent={setImageContent} />
      ) : null}
    </div>
  );
};

export default ProjectModal;
